import { Helmet } from 'react-helmet-async';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import ProductDetails from '../components/product/productDetails/ProductDetails';
import { getProductByParam, getProductPath } from '@/core/global-data/products.data';
import MainSection from '@/components/MainSection';
import { useLocalizedPath } from '@/lib/i18n/useLocale';

export default function ProductPage() {
    const { t, i18n } = useTranslation();
    const { slug: param } = useParams();
    const localizedPath = useLocalizedPath();
    const product = getProductByParam(param);

    if (!product) {
        return <Navigate to={localizedPath('/products')} replace />;
    }

    // Preserve SEO equity: redirect numeric IDs to clean slug URLs.
    if (param && param !== product.slug) {
        return <Navigate to={localizedPath(getProductPath(product))} replace />;
    }

    const HeroIcon = (product.svg || product.background) as ComponentType<{ className?: string }> | undefined;
    const title = t(`products.${product.id}.title`);
    const mainDescription = t(`products.${product.id}.mainDescription`);
    const subtitle = product.hasSubtitle ? t(`products.${product.id}.subtitle`) : '';
    const canonicalPath = `/${i18n.language}${getProductPath(product)}`;

    return (
        <>
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={mainDescription} />
                <link rel="canonical" href={canonicalPath} />
            </Helmet>
            <MainSection
                title={title}
                description={subtitle || mainDescription}
                icon={
                    HeroIcon ? (
                        <HeroIcon className="h-[180px] w-[180px] object-contain sm:h-[200px] sm:w-[210px]" />
                    ) : undefined
                }
            />

            <div className="relative mb-12 w-full overflow-hidden mainContainer lg:mb-[100px]">
                <ProductDetails product={product} />
            </div>
        </>
    );
}
